import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import {
  FormAggregationEntity,
  normalizeFormAggregation,
} from 'entities/FormAggregation';
import { FormAggregationRow } from 'entities/FormAggregationRow';
import { TableRowEntity } from 'entities/TableRow';
import {
  FormAggregationRequestApi,
  FormAggregationResponseApi,
} from 'entities/endpoints/form.aggregation_get';
import { ApiRequest } from 'stores/common/ApiRequest';
import { ILocalStore } from 'stores/interfaces';

import { TableFactoryRowModel } from '../../TableFactoryRowModel';
import { ITableFactoryStore } from '../interface';

import { ITableAggregationFactoryStore } from './interface';

type TableAggregationParams = {
  factoryStore: ITableFactoryStore;
};

type PrivateFields = '_aggregationData' | '_aggregationFooterRowModel';

class TableAggregationFactoryStore
  implements ILocalStore, ITableAggregationFactoryStore
{
  constructor({ factoryStore }: TableAggregationParams) {
    this._factoryStore = factoryStore;

    makeObservable<TableAggregationFactoryStore, PrivateFields>(this, {
      _aggregationData: observable.ref,
      aggregationData: computed,

      _aggregationFooterRowModel: observable.ref,
      aggregationFooterRowModel: computed,
      aggregationRow: computed,
      updateAggregationRow: action.bound,

      aggregationFooterRow: computed,
      updateAggregationFooterRow: action.bound,

      fetchAggregation: action.bound,
    });
  }

  private readonly _factoryStore: ITableFactoryStore;

  readonly aggregationRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.aggregation,
  });

  private _aggregationData: FormAggregationEntity | null = null;

  private _aggregationFooterRowModel: TableFactoryRowModel | null = null;

  get aggregationData() {
    return this._aggregationData;
  }

  get aggregationRow(): FormAggregationEntity['aggregationRow'] | null {
    return this._aggregationData?.aggregationRow;
  }

  get aggregationFooterRow(): FormAggregationEntity['footerRow'] | null {
    return this._aggregationData?.footerRow || null;
  }

  get aggregationFooterRowModel(): TableFactoryRowModel | null {
    return this._aggregationFooterRowModel;
  }

  updateAggregationFooterRow(newRow: TableRowEntity): void {
    this._aggregationData = {
      ...this._aggregationData,
      footerRow: newRow,
    };

    if (this._aggregationFooterRowModel) {
      // Update existing model
      this._aggregationFooterRowModel.updateEntity(newRow);
    } else {
      // Create new model only if it doesn't exist
      this._aggregationFooterRowModel = new TableFactoryRowModel({
        formTableStore: this._factoryStore,
        entity: newRow,
        parent: null,
      });
    }
  }

  updateAggregationRow(newRows: FormAggregationRow[]): void {
    this._aggregationData = {
      ...this._aggregationData,
      aggregationRow: newRows,
    };
  }

  async fetchAggregation(): Promise<void> {
    if (!this._factoryStore.callParams?.form_id) {
      return;
    }

    const { form_id, root_record_id, view } = this._factoryStore.callParams;

    const { isError, data } = await this.aggregationRequest.call<
      FormAggregationResponseApi,
      FormAggregationRequestApi
    >({
      data: {
        form_id,
        root_record_id,
        view,
      },
    });

    if (!data || isError) {
      return;
    }

    runInAction(() => {
      const normalized = normalizeFormAggregation(data);
      this._aggregationData = normalized;

      // Destroy old model before creating new one
      if (this._aggregationFooterRowModel) {
        this._aggregationFooterRowModel.destroy();
        this._aggregationFooterRowModel = null;
      }

      // Create new model if footer row exists
      if (normalized.footerRow) {
        this._aggregationFooterRowModel = new TableFactoryRowModel({
          formTableStore: this._factoryStore,
          entity: normalized.footerRow,
          parent: null,
        });
      }
    });
  }

  destroy() {
    if (this._aggregationFooterRowModel) {
      this._aggregationFooterRowModel.destroy();
      this._aggregationFooterRowModel = null;
    }
    this.aggregationRequest.destroy();
  }
}

export default TableAggregationFactoryStore;
